import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

import { OnboardingShell } from '../../components/venue/onboarding/OnboardingShell';
import { PremiumField } from '../../components/venue/onboarding/PremiumField';
import { AddressAutocomplete } from '../../components/venue/onboarding/AddressAutocomplete';
import { PinConfirmMap } from '../../components/venue/onboarding/PinConfirmMap';
import { resolveStepCounter } from '../../utils/venueOnboardingProgress';
import { geocodeAddress, reverseGeocode } from '../../utils/googleGeocoder';
import { useOnboardingSaveActions } from '../../utils/useOnboardingSaveActions';

const COORDS_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*$/;

const parseCoordinates = (text) => {
  const match = COORDS_REGEX.exec(text || '');
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { latitude: lat, longitude: lng };
};

const formatCoordsForDraft = (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return '';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const loadDraft = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    const parsedCoords = parseCoordinates(saved.coordinates || '');
    return {
      searchQuery: saved.addressSearchQuery || '',
      country: saved.country || '',
      region: saved.region || '',
      city: saved.city || '',
      zipCode: saved.zipCode || '',
      street: saved.street || '',
      coordinatesText: saved.coordinates || '',
      latitude: parsedCoords?.latitude ?? null,
      longitude: parsedCoords?.longitude ?? null,
      googlePlaceId: saved.googlePlaceId || '',
      aade: saved.aade || null,
      aadePinAutoPlaced: !!saved.aadePinAutoPlaced,
    };
  } catch {
    return {
      searchQuery: '',
      country: '',
      region: '',
      city: '',
      zipCode: '',
      street: '',
      coordinatesText: '',
      latitude: null,
      longitude: null,
      googlePlaceId: '',
      aade: null,
      aadePinAutoPlaced: false,
    };
  }
};

const buildAadeAddressQuery = (aade, addr) => {
  // Prefer explicit address fields already stashed by Step 1 (street +
  // zip + city). Fall back to the raw AADE address if those are empty
  // (rare — AADE nearly always returns all three).
  const line1 = (addr.street || '').trim();
  const parts = [line1, (addr.zipCode || '').trim(), (addr.city || '').trim(), (addr.country || 'Greece').trim()]
    .filter(Boolean);
  const query = parts.join(', ');
  if (query) return query;
  return aade?.legalName || '';
};

export default function VenueOnboardingStep2() {
  const navigate = useNavigate();
  const { step, total } = resolveStepCounter(2);
  const [state, setState] = useState(loadDraft);
  const [submitting, setSubmitting] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  // Tracks whether the pin currently on the map was auto-placed from the
  // AADE address on this visit. Used to swap the status banner copy so
  // owners know the pin came from their tax record and needs a quick
  // eyeball confirmation.
  const [pinFromAade, setPinFromAade] = useState(false);
  const [aadeGeocodeLoading, setAadeGeocodeLoading] = useState(false);
  const dragInProgress = useRef(false);
  const aadeAutoAttempted = useRef(false);

  // Persist draft on change.
  useEffect(() => {
    const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...existing,
      addressSearchQuery: state.searchQuery,
      country: state.country,
      region: state.region,
      city: state.city,
      zipCode: state.zipCode,
      street: state.street,
      coordinates: state.coordinatesText,
      googlePlaceId: state.googlePlaceId || '',
      aadePinAutoPlaced: state.aadePinAutoPlaced || false,
    }));
  }, [state]);

  // Auto-place the pin from the AADE address on first load. Runs only
  // when: we have an AADE-supplied address, no pin has been set yet on
  // this venue, and we haven't already auto-placed one for this session.
  // The owner is still asked to confirm/drag it below.
  useEffect(() => {
    if (aadeAutoAttempted.current) return;
    if (state.aadePinAutoPlaced) return;
    if (typeof state.latitude === 'number' && typeof state.longitude === 'number') return;
    if (!state.aade || !(state.street || state.city)) return;

    aadeAutoAttempted.current = true;
    const query = buildAadeAddressQuery(state.aade, {
      street: state.street,
      zipCode: state.zipCode,
      city: state.city,
      country: state.country,
    });
    if (!query) return;

    let cancelled = false;
    setAadeGeocodeLoading(true);
    (async () => {
      try {
        const feature = await geocodeAddress(query);
        if (cancelled || !feature) return;
        const { components, latitude, longitude } = feature;
        const streetLine = [components.street, components.house_number].filter(Boolean).join(' ');
        setState((prev) => ({
          ...prev,
          latitude,
          longitude,
          coordinatesText: formatCoordsForDraft(latitude, longitude),
          // Only fill EMPTY fields — never clobber values already
          // populated by AADE (Step 1) or edited by the owner.
          country: prev.country || components.country || '',
          region: prev.region || components.region || '',
          city: prev.city || components.city || '',
          zipCode: prev.zipCode || components.postcode || '',
          street: prev.street || streetLine || '',
          googlePlaceId: feature.google_place_id || '',
          aadePinAutoPlaced: true,
        }));
        setPinFromAade(true);
      } catch (err) {
        // Silent fail — owner falls back to the manual autocomplete flow.
        console.warn('AADE address geocode failed:', err);
      } finally {
        if (!cancelled) setAadeGeocodeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally run once on mount — state changes shouldn't retrigger
    // the auto-placement or we'd fight the owner's manual pin drags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // When user selects an autocomplete suggestion.
  const handleSuggestionSelect = useCallback((feature) => {
    if (!feature) return;
    const { components, latitude, longitude } = feature;
    const streetLine = [components.street, components.house_number].filter(Boolean).join(' ');
    setPinFromAade(false);
    update({
      searchQuery: feature.title + (feature.subtitle ? `, ${feature.subtitle}` : ''),
      country: components.country || '',
      region: components.region || '',
      city: components.city || '',
      zipCode: components.postcode || '',
      street: streetLine || components.name || '',
      latitude,
      longitude,
      coordinatesText: formatCoordsForDraft(latitude, longitude),
      googlePlaceId: feature.google_place_id || '',
    });
  }, [update]);

  // When user types into the coordinates fallback field.
  const handleCoordsChange = useCallback(async (text) => {
    const parsed = parseCoordinates(text);
    if (!parsed) {
      update({ coordinatesText: text });
      return;
    }
    setPinFromAade(false);
    update({
      coordinatesText: text,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      // A raw coordinate paste detaches from any previously selected place.
      googlePlaceId: '',
    });
    // Reverse-geocode so the address fields get auto-filled — this is the
    // Peloponnese / hidden-gem flow: paste coordinates → form populates.
    setReverseLoading(true);
    try {
      const feature = await reverseGeocode(parsed.latitude, parsed.longitude);
      if (feature) {
        const { components } = feature;
        const streetLine = [components.street, components.house_number].filter(Boolean).join(' ');
        update({
          country: components.country || '',
          region: components.region || '',
          city: components.city || '',
          zipCode: components.postcode || '',
          street: streetLine || components.name || '',
        });
      }
    } catch (err) {
      console.warn('Reverse geocode (paste) failed:', err);
    } finally {
      setReverseLoading(false);
    }
  }, [update]);

  // When the user drags the map pin.
  const handlePinChange = useCallback(async (lat, lng) => {
    dragInProgress.current = true;
    setPinFromAade(false);
    update({
      latitude: lat,
      longitude: lng,
      coordinatesText: formatCoordsForDraft(lat, lng),
      // A dragged pin detaches the venue from any previously selected
      // Google place. Clear the place_id so we don't save a stale one
      // that points at the original address.
      googlePlaceId: '',
    });

    // Reverse-geocode to refresh address fields the user can override.
    setReverseLoading(true);
    try {
      const feature = await reverseGeocode(lat, lng);
      if (feature) {
        const { components } = feature;
        const streetLine = [components.street, components.house_number].filter(Boolean).join(' ');
        update({
          country: components.country || '',
          region: components.region || '',
          city: components.city || '',
          zipCode: components.postcode || '',
          street: streetLine || components.name || '',
        });
      }
    } catch (err) {
      // Reverse geocoding is a nice-to-have — silently ignore failures.
      console.warn('Reverse geocode failed:', err);
    } finally {
      setReverseLoading(false);
      dragInProgress.current = false;
    }
  }, [update]);

  const hasCoordinates = typeof state.latitude === 'number' && typeof state.longitude === 'number';

  const isValid =
    state.country.trim()
    && state.city.trim()
    && state.street.trim()
    && hasCoordinates;

  const { autoSave } = useOnboardingSaveActions();

  const handleNext = async () => {
    if (!isValid) {
      toast.error('Please pick your venue on the map');
      return;
    }

    setSubmitting(true);
    // Make sure the freshly-typed address/coordinates are flushed to
    // sessionStorage before the shared helper reads it.
    const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...existing,
      country: state.country,
      region: state.region,
      city: state.city,
      zipCode: state.zipCode,
      street: state.street,
      coordinates: formatCoordsForDraft(state.latitude, state.longitude),
      googlePlaceId: state.googlePlaceId || '',
    }));
    const result = await autoSave(2);
    setSubmitting(false);
    if (!result.ok) {
      toast.error('Failed to save. Please try again.');
      return;
    }
    // Venue Vibe (categories/tags) and Operating Hours moved to the
    // post-agreement Setup Mode checklist — Step 2 (Location) is now
    // the last quick-wizard step before the Agreement.
    navigate('/venue/onboarding/agreement');
  };

  return (
    <OnboardingShell
      testId="venue-step3"
      step={step}
      totalSteps={total}
      title="Where is your venue?"
      subtitle="Search your address, drop a pin, or paste coordinates — guests will see this exact location on the Allure map."
      primaryLabel={hasCoordinates ? 'Confirm location' : 'Pick a location'}
      primaryTestId="venue-step2-next"
      primaryDisabled={!isValid}
      primaryLoading={submitting}
      onPrimary={handleNext}
      onBack={() => navigate('/venue/onboarding/1')}
      contentMaxWidth={420}
    >
      <AddressAutocomplete
        value={state.searchQuery}
        onChange={(v) => update({ searchQuery: v })}
        onSelect={handleSuggestionSelect}
        testId="venue-step2-address-search"
      />

      <div
        style={{
          background: hasCoordinates ? 'var(--status-success-surface)' : 'var(--status-warning-surface)',
          border: hasCoordinates ? '1px solid rgba(31, 142, 42, 0.18)' : '1px solid rgba(231, 158, 38, 0.20)',
          borderRadius: 14,
          padding: '10px 14px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
        data-testid="venue-step2-status"
      >
        {hasCoordinates ? (
          <CheckCircle2 size={18} color="var(--status-success-text)" strokeWidth={2.2} />
        ) : (
          <MapPin size={18} color="var(--status-warning-text)" strokeWidth={2.2} />
        )}
        <p
          style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: 13,
            fontWeight: 600,
            color: hasCoordinates ? 'var(--status-success-text)' : 'var(--status-warning-text)',
          }}
          data-testid={pinFromAade ? 'venue-step2-status-aade' : undefined}
        >
          {aadeGeocodeLoading
            ? 'Placing your pin from AADE address…'
            : pinFromAade && hasCoordinates
              ? "We placed the pin from your AADE address — please confirm it, and drag if it's off."
              : hasCoordinates
                ? 'Pin placed. Drag it on the map to fine-tune the exact spot.'
                : 'Pick a suggestion or paste coordinates below to drop the pin.'}
        </p>
      </div>

      <PinConfirmMap
        latitude={state.latitude}
        longitude={state.longitude}
        onChange={handlePinChange}
        testId="venue-step2-pin-map"
        height={260}
      />
      {reverseLoading ? (
        <p
          data-testid="venue-step2-reverse-loading"
          style={{ margin: '8px 4px 4px', fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)' }}
        >
          Updating address from the new pin location…
        </p>
      ) : null}

      <div style={{ marginTop: 22 }}>
        <p
          style={{
            margin: '0 0 10px 4px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 12.5,
            fontWeight: 700,
            color: 'var(--accent-purple)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          Address details
        </p>
        <PremiumField
          label="Street + number"
          value={state.street}
          onChange={(v) => update({ street: v })}
          placeholder="e.g. Eth. Antistaseos 21"
          testId="venue-step2-street"
          required
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <PremiumField
            label="City"
            value={state.city}
            onChange={(v) => update({ city: v })}
            placeholder="e.g. Corfu"
            testId="venue-step2-city"
            required
          />
          <PremiumField
            label="Zip code"
            value={state.zipCode}
            onChange={(v) => update({ zipCode: v })}
            placeholder="e.g. 49100"
            testId="venue-step2-zip"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <PremiumField
            label="Region / State"
            value={state.region}
            onChange={(v) => update({ region: v })}
            placeholder="e.g. Ionian Islands"
            testId="venue-step2-region"
          />
          <PremiumField
            label="Country"
            value={state.country}
            onChange={(v) => update({ country: v })}
            placeholder="e.g. Greece"
            testId="venue-step2-country"
            required
          />
        </div>
      </div>

      <div style={{ marginTop: 6 }}>
        <PremiumField
          label="Coordinates (fallback)"
          value={state.coordinatesText}
          onChange={handleCoordsChange}
          placeholder="e.g. 39.6243, 19.9217"
          testId="venue-step2-coordinates"
          hint="Tip: for hidden gems without a street address, paste GPS coordinates here and the pin will jump there. You can still drag it to fine-tune."
        />
      </div>
    </OnboardingShell>
  );
}
